import { useState } from "react";
import { BadgeCheck, Ban, ExternalLink, Send } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { api, fetchCollection, formatApiError } from "@/lib/api";
import { formatDate } from "@/utils/format";

const STATUS = {
  pending_payment: ["Pago pendiente", "text-slate-400 border-white/15"],
  free: ["Gratuita", "text-slate-400 border-white/15"],
  pending_review: ["Pendiente de revisión", "text-brass border-brass/40 bg-brass/10"],
  approved: ["Aprobada · en portada", "text-clay border-clay/40 bg-clay/10"],
  rejected: ["Rechazada", "text-red-200 border-red-500/30 bg-red-500/10"],
};

export function SponsoredAdmin() {
  const queryClient = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["admin-sponsored"], queryFn: () => fetchCollection("/admin/sponsored") });
  const telegram = useQuery({ queryKey: ["telegram-status"], queryFn: () => fetchCollection("/admin/telegram/status") });
  const [filter, setFilter] = useState("all");
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-sponsored"] });
    queryClient.invalidateQueries({ queryKey: ["sponsored-active"] });
  };
  const review = useMutation({
    mutationFn: async ({ id, approve }) => (await api.post(`/admin/sponsored/${id}/${approve ? "approve" : "reject"}`, approve ? undefined : { reason: "Rechazada desde el panel" })).data,
    onSuccess: (shoot) => { toast.success(shoot.status === "approved" ? "Tirada aprobada y publicada en portada" : "Tirada rechazada"); refresh(); },
    onError: (error) => toast.error(formatApiError(error)),
  });
  const setup = useMutation({
    mutationFn: async () => (await api.post("/admin/telegram/setup")).data,
    onSuccess: (data) => { toast.success(`Telegram conectado (chat ${data.chat_id})`); queryClient.invalidateQueries({ queryKey: ["telegram-status"] }); },
    onError: (error) => toast.error(formatApiError(error)),
  });
  const visible = data.filter((item) => filter === "all" || item.status === filter);

  return (
    <div className="grid gap-6" data-testid="admin-sponsored-panel">
      <div className="flex flex-col justify-between gap-4 border border-white/10 bg-white/[0.03] p-5 md:flex-row md:items-center" data-testid="telegram-status-card">
        <div>
          <p className="eyebrow">Aviso al móvil</p>
          <p className="mt-2 text-sm text-slate-300" data-testid="telegram-status-text">
            {telegram.data?.configured ? (telegram.data.chat_id ? `Telegram conectado · chat ${telegram.data.chat_id}` : "Bot configurado. Envía /start al bot y pulsa «Conectar Telegram».") : "Falta TELEGRAM_BOT_TOKEN en el servidor. Crea un bot con @BotFather y facilita el token."}
          </p>
        </div>
        <Button type="button" onClick={() => setup.mutate()} disabled={setup.isPending || !telegram.data?.configured} variant="outline" className="rounded-none border-clay/40 bg-clay/10 font-mono text-xs uppercase tracking-[0.2em] text-clay hover:bg-clay hover:text-charcoal" data-testid="telegram-setup-button">
          {setup.isPending ? "Conectando..." : "Conectar Telegram"} <Send className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="sponsored-filter-group">
        {[["all", "Todas"], ["pending_review", "Pendientes"], ["approved", "Aprobadas"], ["rejected", "Rechazadas"], ["pending_payment", "Sin pagar"]].map(([value, label]) => (
          <button key={value} type="button" onClick={() => setFilter(value)} className={`filter-chip ${filter === value ? "filter-chip-active" : ""}`} data-testid={`sponsored-filter-${value}`}>{label}</button>
        ))}
      </div>

      {isLoading ? <p className="text-sm text-slate-500">Cargando patrocinios...</p> : null}
      {!isLoading && visible.length === 0 ? <p className="border border-dashed border-white/15 p-6 text-sm text-slate-500" data-testid="sponsored-empty-state">No hay tiradas patrocinadas en este estado.</p> : null}

      <div className="grid gap-4">
        {visible.map((item) => {
          const [label, classes] = STATUS[item.status] || [item.status, ""];
          return (
            <article key={item.id} className="grid gap-4 border border-white/10 bg-white/[0.025] p-4 md:grid-cols-[110px_1fr_auto] md:items-center" data-testid={`sponsored-item-${item.id}`}>
              <a href={`${process.env.REACT_APP_BACKEND_URL}${item.poster_url}`} target="_blank" rel="noreferrer" data-testid={`sponsored-poster-${item.id}`}>
                {item.poster_path.endsWith(".pdf") ? <span className="grid h-28 w-full place-items-center border border-white/10 font-mono text-[10px] uppercase text-slate-400">PDF</span> : <img src={`${process.env.REACT_APP_BACKEND_URL}${item.poster_url}`} alt={item.title} className="h-28 w-full object-cover" />}
              </a>
              <div className="min-w-0">
                <span className={`inline-block border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${classes}`} data-testid={`sponsored-status-${item.id}`}>{label}</span>
                <h5 className="mt-2 text-base font-bold text-slate-100">{item.title}</h5>
                <p className="mt-1 text-sm text-slate-400">{formatDate(item.date)} {item.time} · {item.location} ({item.province}) · {item.modality}{item.targets ? ` · ${item.targets} platos` : ""}</p>
                <p className="mt-1 text-xs text-slate-500">{item.organizer_name} · <a href={`tel:${item.organizer_phone}`} className="hover:text-clay">{item.organizer_phone}</a> · <a href={`mailto:${item.organizer_email}`} className="hover:text-clay">{item.organizer_email}</a> · {item.payment_status === "free" ? "publicación gratuita" : `pago: ${item.payment_status}`}</p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                {item.status === "pending_review" || item.status === "rejected" ? (
                  <Button type="button" onClick={() => review.mutate({ id: item.id, approve: true })} disabled={review.isPending} className="rounded-none bg-clay font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal hover:bg-brass" data-testid={`sponsored-approve-${item.id}`}>Aprobar <BadgeCheck className="h-3 w-3" /></Button>
                ) : null}
                {item.status === "pending_review" || item.status === "approved" ? (
                  <Button type="button" onClick={() => window.confirm(`¿Rechazar “${item.title}”?`) && review.mutate({ id: item.id, approve: false })} disabled={review.isPending} variant="outline" className="rounded-none border-red-500/25 bg-red-500/5 font-mono text-[10px] uppercase tracking-[0.18em] text-red-200 hover:bg-red-500/20" data-testid={`sponsored-reject-${item.id}`}>Rechazar <Ban className="h-3 w-3" /></Button>
                ) : null}
                <Button asChild variant="outline" className="rounded-none border-white/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.18em]"><a href={`${process.env.REACT_APP_BACKEND_URL}${item.poster_url}`} target="_blank" rel="noreferrer">Cartel <ExternalLink className="h-3 w-3" /></a></Button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
